import { Home } from "../services/portfolioApi";

type HeatLossCalculation = Record<string, any>
type HeatLossComponent = { label: string; value: number; color: string }

const scenarioConfig: Record<
  string,
  { title: string; description: string }
> = {
  default: {
    title: "Default",
    description: "Typical insulation assumed for this home type from the latest information."
  },
  not_insulated: {
    title: "No insulation",
    description: "Assumes no insulation; windows are partial double glazing"
  },
  insulated: {
    title: "Insulated",
    description: "Upgraded insulation across roof, walls, floor and windows"
  }
}

export const getHeatLossScenarios = (
  heatLossCalculations: HeatLossCalculation[]
): string[] =>
  Array.from(
    new Set(
      heatLossCalculations.map((calc) => calc.calculationScenario).filter(Boolean)
    )
  ) as string[]

export const getScenarioLabel = (scenario: string) =>
  scenarioConfig[scenario]?.title ||
  scenario.charAt(0).toUpperCase() + scenario.slice(1)

export const getScenarioDescription = (scenario: string) =>
  scenarioConfig[scenario]?.description || "Heat loss calculation scenario"

export const getLatestHeatLoss = (
  heatLossCalculations: HeatLossCalculation[],
  selectedScenario: string
) =>
  heatLossCalculations.find(
    (calc) => calc.calculationScenario === selectedScenario
  ) || null

export const buildHeatLossComponents = (
  latestHeatLoss: HeatLossCalculation
): HeatLossComponent[] => {
  const hours = latestHeatLoss?.heatingHoursBaseline || 1
  return [
    { label: "Walls", value: latestHeatLoss?.wallHeatLossWh * hours || 0, color: "blue" },
    { label: "Roof", value: latestHeatLoss?.roofHeatLossWh * hours || 0, color: "green" },
    { label: "Floor", value: latestHeatLoss?.floorHeatLossWh * hours || 0, color: "orange" },
    { label: "Windows", value: latestHeatLoss?.windowHeatLossWh * hours || 0, color: "purple" },
    { label: "Doors", value: latestHeatLoss?.doorHeatLossWh * hours || 0, color: "red" },
    {
      label: "Ventilation",
      value: latestHeatLoss?.ventilationHeatLossWh * hours || 0,
      color: "teal"
    }
  ]
}

export const getScenarioAdjustedLabel = (
  selectedScenario: string,
  defaultLabel: string
) => {
  if (selectedScenario === "insulated") return "insulated"
  if (selectedScenario === "not_insulated") return "not insulated"
  return defaultLabel
}

export const getWindowsLabel = (selectedScenario: string, home: Home) => {
  if (selectedScenario === "not_insulated") return "Partial double glazing"
  if (selectedScenario === "insulated") return "insulated"
  return home.features.windowsType
}

export const getDoorsLabel = () => "uPVC modern door insulated"

export const getDeltaT = (latestHeatLoss: HeatLossCalculation | null) =>
  (latestHeatLoss?.internalDesignTemperature || 0) -
  (latestHeatLoss?.externalDesignTemperature || 0)

export const calculateTotalHeatLoss = (components: HeatLossComponent[]) =>
  components.reduce((sum, component) => sum + component.value, 0)

